/**
 * The one order every multi-pick list shows: what is selected first, then the
 * rest (Gio, 2026-10-17).
 *
 * `alpha` sorts each group by its label, numbers by value ("Act 2" before
 * "Act 10") and case and accents ignored. `ranked` keeps the caller's order in
 * each group, for lists whose order means something (age bands, credit score,
 * education, low to high).
 *
 * The caller decides *when* to apply it. A row that jumps away under the cursor
 * reads as a failed click, so pickers take the selected set as it was when the
 * list opened or the search changed, never the live one.
 */
export type SelectionOrder = 'alpha' | 'ranked';

const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

export function orderSelectedFirst<T>(
  items: readonly T[],
  pinned: ReadonlySet<string>,
  order: SelectionOrder,
  idOf: (item: T) => string,
  labelOf: (item: T) => string,
): T[] {
  const first: T[] = [];
  const rest: T[] = [];
  for (const item of items) (pinned.has(idOf(item)) ? first : rest).push(item);
  if (order === 'alpha') {
    // Array.prototype.sort is stable, so equal labels keep the caller's order.
    const byLabel = (a: T, b: T) => collator.compare(labelOf(a), labelOf(b));
    first.sort(byLabel);
    rest.sort(byLabel);
  }
  return [...first, ...rest];
}
